
import React from "react";
import { useParams } from "react-router-dom";
import { useLoadBlogPost } from "@/hooks/useLoadBlogPost";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import BlogPostLoading from "@/components/blog/BlogPostLoading";
import BlogPostNotFound from "@/components/blog/BlogPostNotFound";
import BlogPostContent from "@/components/blog/BlogPostContent";
import ViewTracker from "@/components/blog/ViewTracker";
import { useBlogSeo } from "@/hooks/useBlogSeo";
import useSEO from "@/hooks/useSEO";

/**
 * Blog post page component
 */
const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { post, isLoading } = useLoadBlogPost(slug);
  // Use a larger number to ensure we get enough random posts after filtering
  const { posts: relatedPostsData } = useBlogPosts({ 
    published: true,
    limit: 10, // Increased to ensure we have enough after filtering
    random: true 
  });

  // Generate SEO props from the blog post
  const seoProps = useBlogSeo(post, slug);
  
  // Use custom SEO hook for direct DOM manipulation instead of SEO component
  useSEO({
    title: seoProps.title,
    description: seoProps.description,
    canonicalUrl: seoProps.canonicalUrl,
    ogImage: seoProps.ogImage,
    ogType: seoProps.ogType,
    noIndex: seoProps.noIndex,
    schema: seoProps.schema
  });

  if (isLoading) {
    return <BlogPostLoading />;
  }
  
  if (!post) {
    return <BlogPostNotFound />;
  }

  // Filter out the current post and get 4 random posts
  const relatedPosts = relatedPostsData
    .filter(relatedPost => relatedPost.id !== post.id)
    .map(post => ({
      id: post.id,
      title: post.title,
      excerpt: post.excerpt,
      slug: post.slug,
      imageSrc: post.featured_image || "https://images.unsplash.com/photo-1607305387299-a3d9611cd469"
    }))
    .slice(0, 4); // Ensure we only pass exactly 4 posts

  return (
    <>
      {/* Only include ViewTracker here and not in BlogPostContent */}
      <ViewTracker post={post} />
      <BlogPostContent 
        post={post}
        relatedPosts={relatedPosts}
        slug={slug}
        skipSeo={true} // Skip SEO in the content component to avoid nesting
        skipViewTracking={true} // Skip view tracking in the content component
      />
    </>
  );
};

export default BlogPost;
